import Button, { ButtonIcon } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconProps } from "@insite/mobius/Icon";
import { MappedLink } from "@insite/mobius/Menu";
import Link from "@insite/mobius/Link";
import PanelRow, { PanelRowPresentationProps } from "@insite/mobius/PanelMenu/PanelRow";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import getColor from "@insite/mobius/utilities/getColor";
import resolveColor from "@insite/mobius/utilities/resolveColor";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React from "react";
import styled, { css, ThemeConsumer, ThemeProps, withTheme } from "styled-components";

export interface PanelMenuPresentationProps {
    /**
     * Indicates how the `css` property is combined with the variant `css` property from the theme.
     * If true, the variant css is applied first and then the component css is applied after causing
     * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
     */
    mergeCss?: boolean;
    /** CSS string or styled-components function to be injected into this component
     * @themable */
    cssOverrides?: {
        wrapper?: StyledProp<PanelMenuPresentationProps>;
        children?: StyledProp<PanelMenuPresentationProps>;
        menu?: StyledProp<PanelMenuPresentationProps>;
    };
    /** Props passed to the typography children of the menu.
     * @themable */
    childTypographyProps?: TypographyPresentationProps;
    /** Props passed to the menu close icon.
     * @themable */
    closeIconProps?: IconProps;
    /** Props passed to the menu back icon.
     * @themable */
    backIconProps?: IconProps;
    /** Color of the background of the menu header.
     * @themable */
    headerColor?: string;
    /** Color of the background of the menu body.
     * @themable */
    bodyColor?: string;
    /** Props to be passed into the panel row component.
     * @themable */
    panelRowProps?: PanelRowPresentationProps;
}

export type PanelMenuComponentProps = MobiusStyledComponentProps<
    "nav",
    {
        /** The element that triggers the menu. */
        panelTrigger: React.ReactElement<{ id: string; onClick: () => void; closeOverlay: () => void }>;
        /** Aria attributes used internally require an `id`. If not provided, a random id is generated. */
        descriptionId?: string;
        /**
         * The nested child structure that will be rendered by the menu.
         * If menu items are provided, children organically passed to the component
         * will not be rendered.
         */
        menuItems?: MappedLink[];
        /** Maximum number of menus that will appear in submenu panels. */
        maxDepth?: number;
        /** Function called to close overlay (Drawer) in which menu resides. Governs presence of `x` icons in headers. */
        closeOverlay?: () => void;
        /** The current URL of the application, used to apply 'current page' styles to the menu item */
        currentUrl?: string;
        /** @ignore internal prop */
        layer: number;
        /** Allow to open parent links */
        allowOpenParentLinks?: boolean;
        /**
         * Delays rendering children until the menu trigger is fired.
         * Useful for performance when rendering extremely large navigation trees.
         */
        delayRenderingChildren?: boolean;
        /**
         * Visible title on slide-in submenu headers (between back and close), e.g. “Back to Main Menu”.
         */
        submenuHeaderTitle?: string;
        /**
         * When drilling into a category’s children, render “All [title]” first (same as desktop mega menu).
         */
        parentCategoryForSubcategoryPanel?: MappedLink;
    }
>;

/** Matches desktop `MainNavigationItem` mega-menu “All [category]” link styling. */
const megaMenuAllCategoryPanelLinkCss = css`
    color: #000 !important;
    background: transparent;
    font-weight: 400;
    text-decoration: none;
    display: block;
    width: 100%;
    padding: 15px 0;

    span {
        color: ${getColor("common.global.black")} !important;
    }

    &:hover span {
        color: ${getColor("primary.main")} !important;
        text-decoration: underline;
        font-weight: 700;
    }
`;

const megaMenuAllCategoryPanelRowCss = css`
    border-bottom: 1px solid ${getColor("common.global.gray03")};
    min-height: 44px;
    display: flex;
    align-items: center;
    padding: 0 20px;
    cursor: pointer;
    &:hover {
        background: ${getColor("common.global.gray04")} !important;
    }
    & a {
        background: transparent !important;
    }
    &:hover .mega-menu-all-link,
    &:hover .mega-menu-all-link span {
        color: ${getColor("primary.main")} !important;
        text-decoration: underline;
        font-weight: 700;
    }
`;

const MegaMenuAllCategoryRow = styled.div`
    ${megaMenuAllCategoryPanelRowCss}
`;

const PanelSlideHeaderInner = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 0 4px;
    gap: 4px;
    background: ${getColor("common.global.black")};

    & > button:first-of-type {
        flex-shrink: 0;
    }
    & > button:last-of-type {
        flex-shrink: 0;
    }
`;

const PanelMenuWrapper = styled.nav<ThemeProps<BaseTheme> & InjectableCss>`
    display: flex;
    align-items: center;

    button,
    link {
        &:focus {
            outline-offset: -${getProp("theme.focus.width")};
        }
    }
    > button {
        width: 100%;
    }
    &:focus > *:first-child {
        outline-color: ${getProp("theme.focus.color", "#09f")};
        outline-style: ${getProp("theme.focus.style", "solid")};
        outline-width: ${getProp("theme.focus.width", "2px")};
        outline-offset: -${getProp("theme.focus.width")};
    }
    outline: none;
    ${injectCss}
`;

const PanelMenuStyle = styled.div<ThemeProps<BaseTheme> & InjectableCss & { color?: string }>`
    height: calc(100% - 50px);
    overflow-y: auto;
    background: ${({ color, theme }) => resolveColor(color, theme)};
    ${injectCss}
`;

const PanelChildren = styled.div<ThemeProps<BaseTheme> & InjectableCss & { displayLayer: number }>`
    max-height: 100vh;
    height: 100%;
    width: 100%;
    position: absolute;
    top: 0;
    left: 100%;
    transition: all ease-in-out ${getProp("theme.transition.duration.short")}ms;
    ${({ displayLayer }) => {
        if (displayLayer <= 0) {
            return css`
                left: 0;
            `;
        }
        return css`
            left: 100%;
        `;
    }}
    ${injectCss}
`;

/** PanelMenuProps with defaulted properties now listed as not-optional, plus theme. */
export type PanelMenuProps = PanelMenuComponentProps & PanelMenuPresentationProps;

type PanelMenuPropsCompleted = PanelMenuPresentationProps &
    Omit<PanelMenuComponentProps, "maxDepth"> &
    Required<Pick<PanelMenuComponentProps, "maxDepth">>;

class PanelMenu extends React.Component<PanelMenuPropsCompleted> {
    static defaultProps = { maxDepth: 3 };

    state = {
        layer: this.props.layer + 1,
        descriptionId: this.props.descriptionId || uniqueId(),
        delayRenderingChildren: this.props.delayRenderingChildren,
    };

    UNSAFE_componentWillReceiveProps(nextProps: PanelMenuPropsCompleted) {
        if (nextProps.layer !== this.props.layer + 1 && !nextProps.children && !this.props.children) {
            this.setState({ layer: nextProps.layer + 1 }, () => {});
        }
    }

    moveNextLeft = () => {
        this.setState(({ layer }: { layer: number }) => {
            return {
                delayRenderingChildren: false,
                layer: layer - 1,
            };
        });
    };

    moveNextRight = () => {
        this.setState(({ layer }: { layer: number }) => {
            return { layer: layer + 1 };
        });
    };

    renderMenuItems(childMenuItems: MappedLink[], currentDepth: number, propBuilder: any, translate: (key: string) => string) {
        if (this.state.delayRenderingChildren) {
            return null;
        }
        const { applyProp } = propBuilder;
        const { currentUrl, allowOpenParentLinks, parentCategoryForSubcategoryPanel, closeOverlay } = this.props;
        const typographyProps = applyProp("childTypographyProps");
        const panelRowProps = applyProp("panelRowProps");

        return (
            <>
                {parentCategoryForSubcategoryPanel?.url && (
                    <MegaMenuAllCategoryRow key="mega-menu-all-category">
                        <Link
                            className="mega-menu-all-link"
                            css={megaMenuAllCategoryPanelLinkCss}
                            href={parentCategoryForSubcategoryPanel.url}
                            onClick={closeOverlay}
                            data-test-selector={`all_${parentCategoryForSubcategoryPanel.title}`}
                        >
                            {translate("All")} {parentCategoryForSubcategoryPanel.title}
                        </Link>
                    </MegaMenuAllCategoryRow>
                )}
                {childMenuItems
                    .filter(item => !item.excludeFromNavigation)
                    .map(item => {
                        const renderChildren =
                            item.children?.filter(child => !child.excludeFromNavigation).length &&
                            currentDepth < this.props.maxDepth &&
                            !parentCategoryForSubcategoryPanel;
                        const isCurrent = !!currentUrl && (item.url === currentUrl || item.title === currentUrl);

                        const clickableProps: { href?: string } = { ...item.clickableProps };
                        if ((!renderChildren || allowOpenParentLinks) && item.url && !isCurrent) {
                            clickableProps.href = item.url;
                        }

                        const menuItem = (
                            <PanelRow
                                key={item.title}
                                isCurrent={isCurrent}
                                onClick={this.props.closeOverlay}
                                color={applyProp("bodyColor")}
                                hasChildren={renderChildren || false}
                                allowOpenParentLinks={allowOpenParentLinks}
                                tabIndex={this.state.layer === 0 ? 0 : -1}
                                css={css`
                                    width: 100%;
                                    border-bottom: 1px solid ${getColor("common.global.gray03")};
                                    height: 48px !important;
                                `}
                                {...clickableProps}
                                {...panelRowProps}
                            >
                                <Typography
                                    data-test-selector={`${this.props["data-test-selector"]}_${item.title}`}
                                    size={15}
                                    {...typographyProps}
                                >
                                    {item.title}
                                </Typography>
                            </PanelRow>
                        );
                        if (renderChildren) {
                            return (
                                <PanelMenu
                                    {...this.props}
                                    as="div"
                                    key={item.title}
                                    panelTrigger={menuItem}
                                    delayRenderingChildren={true}
                                    menuItems={item.children as MappedLink[]}
                                    maxDepth={this.props.maxDepth - 1}
                                    layer={this.state.layer}
                                    submenuHeaderTitle={translate("Back to All Categories")}
                                    parentCategoryForSubcategoryPanel={item}
                                />
                            );
                        }
                        return menuItem;
                    })}
            </>
        );
    }

    render() {
        return (
            <ThemeConsumer>
                {theme => {
                    const {
                        currentUrl,
                        descriptionId,
                        menuItems,
                        panelTrigger,
                        closeOverlay,
                        mergeCss,
                        submenuHeaderTitle,
                        parentCategoryForSubcategoryPanel,
                        ...otherProps
                    } = this.props;
                    const propBuilder = applyPropBuilder({ ...this.props, theme }, { component: "panelMenu" });
                    const { translate } = theme;
                    const { layer, descriptionId: navId } = this.state;
                    const { spreadProps, applyProp, applyStyledProp } = propBuilder;
                    const resolvedMergeCss = mergeCss ?? otherProps?.theme?.panelMenu?.defaultProps?.mergeCss;
                    const headerColor = applyProp("headerColor", "common.backgroundContrast");
                    return (
                        <PanelMenuWrapper
                            css={applyStyledProp(["cssOverrides", "wrapper"], resolvedMergeCss)}
                            {...{ "aria-labelledby": navId }}
                            {...otherProps}
                            tabIndex={-1}
                        >
                            {React.cloneElement(panelTrigger, { id: navId, onClick: this.moveNextLeft, closeOverlay })}
                            <PanelChildren
                                displayLayer={layer}
                                aria-hidden={layer > 0}
                                css={applyStyledProp(["cssOverrides", "children"], resolvedMergeCss)}
                                data-layer={layer}
                            >
                                <PanelRow as="div" color={headerColor} header>
                                    <PanelSlideHeaderInner>
                                        <Button
                                            color={headerColor}
                                            css={css`
                                                border: none;
                                                background: ${getColor("common.global.black")};
                                            `}
                                            onClick={this.moveNextRight}
                                            tabIndex={layer === 0 ? 0 : -1}
                                        >
                                            <ButtonIcon {...spreadProps("backIconProps")} />
                                            <VisuallyHidden>{translate("Back")}</VisuallyHidden>
                                        </Button>
                                        {submenuHeaderTitle && (
                                            <Typography
                                                size={14}
                                                css={css`
                                                    flex: 1;
                                                    text-align: start;
                                                    font-weight: 700;
                                                    font-size: 15px;
                                                    line-height: 100%;
                                                    padding: 16px 4px;
                                                `}
                                            >
                                                {submenuHeaderTitle}
                                            </Typography>
                                        )}
                                        {closeOverlay && (
                                            <Button
                                                color={headerColor}
                                                css={css`
                                                    border: none;
                                                    background: ${getColor("common.global.black")};
                                                `}
                                                onClick={closeOverlay}
                                                tabIndex={layer === 0 ? 0 : -1}
                                            >
                                                <ButtonIcon {...spreadProps("closeIconProps")} />
                                                <VisuallyHidden>{translate("Close")}</VisuallyHidden>
                                            </Button>
                                        )}
                                    </PanelSlideHeaderInner>
                                </PanelRow>
                                <PanelMenuStyle
                                    css={applyStyledProp(["cssOverrides", "menu"], resolvedMergeCss)}
                                    color={applyProp("bodyColor", "common.accent")}
                                >
                                    {menuItems ? (
                                        this.renderMenuItems(menuItems, 1, propBuilder, translate)
                                    ) : (
                                        <>{this.props.children}</>
                                    )}
                                </PanelMenuStyle>
                            </PanelChildren>
                        </PanelMenuWrapper>
                    );
                }}
            </ThemeConsumer>
        );
    }
}

/**
 * Menu composed of slide-in panels.
 */

PanelMenu.defaultProps = {
    maxDepth: 3,
};

/** @component */
export default withTheme((props: PanelMenuProps) => <PanelMenu {...(props as PanelMenuPropsCompleted)} />);
